import React, { useState, useEffect, useCallback } from 'react';
import { withRouter, useHistory } from 'react-router-dom';
import {
  A,
  Box,
  Button,
  Heading,
  LoadingSpinner,
  TextInput,
  sendNotification,
} from 'mb-components';
import { UserListItem } from './styled';

interface ManageGroupProps {
  match?: any;
  location?: any;
}

const ManageGroupPage: React.FC<ManageGroupProps> = ({ match }) => {
  const history = useHistory();
  const [applicationUsers, setApplicationUsers] = useState<string[]>();
  const [users, setUsers] = useState<string[]>([]);
  const [groupId] = useState(match.params.groupId);
  const [newUser, setNewUser] = useState('');
  const [usersHaveChanged, setUsersHaveChanged] = useState(false);
  const [isAddLoading, setAddLoading] = useState(false);
  const [isSaveLoading, setSaveLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  useEffect(() => {
    getApplicationUsers();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (applicationUsers) {
      setUsers(applicationUsers);
      setUsersHaveChanged(false);
    }
  }, [applicationUsers]);

  const sendSuccessfulNotification = useCallback((msg: string) => {
    sendNotification({
      message: msg,
      type: 'success',
      dismiss: {
        duration: 3500,
        showIcon: true,
        click: true,
      },
    });
  }, []);

  const sendErrorNotification = useCallback((msg: string) => {
    sendNotification({
      message: msg,
      type: 'danger',
      dismiss: {
        duration: 3500,
        showIcon: true,
        click: true,
      },
    });
  }, []);

  const getApplicationUsers = useCallback(() => {
    fetch(
      `${process.env.REACT_APP_ADMIN_BACKEND}/1/applications/${groupId}/field/users`,
      {
        credentials: 'include',
      },
    )
      .then((response) => {
        if (!response.ok) {
          throw response;
        }
        return response.json();
      })
      .then((responseBody) => {
        setApplicationUsers(responseBody.application.users);
      })
      .catch((err) => {
        history.push('/manage-groups');
      });
  }, [groupId, history]);

  const addUser = useCallback(
    (newUser: string) => {
      if (users.includes(newUser)) {
        sendErrorNotification('User already exists in group');
      } else {
        setUsers((users) =>
          users.includes(newUser) ? users : [...users, newUser],
        );
        setUsersHaveChanged(true);
      }
      setNewUser('');
    },
    [sendErrorNotification, users],
  );

  const removeUsers = useCallback((selectedUsers: string[]) => {
    setUsers((users) => users.filter((user) => !selectedUsers.includes(user)));
    setSelectedUsers([]);
    setUsersHaveChanged(true);
  }, []);

  const addUserIfExists = useCallback(
    (user: string) => {
      setAddLoading(true);
      fetch(`${process.env.REACT_APP_ADMIN_BACKEND}/1/user/${user}`, {
        method: 'GET',
        credentials: 'include',
      })
        .then((response) => {
          if (!response.ok) {
            throw response;
          }
          return response.json();
        })
        .then(() => {
          addUser(user);
          setAddLoading(false);
        })
        .catch((err) => {
          err.json().then((errorData: any) => {
            sendErrorNotification(errorData.message);
            setAddLoading(false);
          });
        });
    },
    [addUser, sendErrorNotification],
  );

  const saveGroup = useCallback(
    (users: any) => {
      setSaveLoading(true);
      const saveData = {
        users: users,
      };
      fetch(
        `${process.env.REACT_APP_ADMIN_BACKEND}/1/applications/${groupId}/field/users`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(saveData),
        },
      )
        .then((response) => {
          if (!response.ok) {
            throw response;
          }
          return response.json();
        })
        .then((data) => {
          setSaveLoading(false);
          sendSuccessfulNotification(
            'Group updated successfuly. The change will take effect when the affected users next log in.',
          );
          getApplicationUsers();
        })
        .catch((err) => {
          err.json().then((errorData: any) => {
            setSaveLoading(false);
            sendErrorNotification(errorData.message);
          });
        });
    },
    [
      getApplicationUsers,
      groupId,
      sendErrorNotification,
      sendSuccessfulNotification,
    ],
  );

  const selectUser = (selectedUser: string) => {
    if (selectedUsers.includes(selectedUser)) {
      setSelectedUsers(selectedUsers.filter((user) => user !== selectedUser));
    } else {
      setSelectedUsers([...selectedUsers, selectedUser]);
    }
  };

  return (
    <>
      {applicationUsers ? (
        <>
          <Box direction={'column'} width={'full'} align={'start'}>
            <Box direction="row" width="full" align="center" justify="between">
              <A to={`/manage-groups`}>&lt; Back</A>
              <Button
                onClick={() => saveGroup(users)}
                disabled={!usersHaveChanged}
              >
                {isSaveLoading ? (
                  <LoadingSpinner
                    size="small"
                    colors={{
                      first: 'white',
                      second: 'white',
                    }}
                  />
                ) : (
                  <>Save</>
                )}
              </Button>
            </Box>
            <Heading>Manage</Heading>

            <Box direction={'column'} width={'full'} margin={{ bottom: '4' }}>
              {users.map((user) => {
                return (
                  <UserListItem key={user}>
                    <input
                      type="checkbox"
                      id={`checkbox-${user}`}
                      onChange={() => selectUser(user)}
                    />
                    {user}
                  </UserListItem>
                );
              })}
            </Box>
            <Button
              onClick={() => removeUsers(selectedUsers)}
              disabled={!selectedUsers.length}
            >
              Remove Users
            </Button>
          </Box>
          <Box
            background={'gray-300'}
            direction={'column'}
            align={'start'}
            margin={{
              top: `6`,
              right: `-1.5rem`,
              bottom: `-1.5rem`,
              left: `-1.5rem`,
            }}
            padding={'6'}
          >
            <Box margin={{ bottom: '3' }} direction={'column'} width={'full'}>
              <TextInput
                background={'white'}
                type={'text'}
                name={'email'}
                placeholder={'Email'}
                value={newUser}
                onChange={(e: any) => setNewUser(e.target.value)}
              />
            </Box>
            <Button
              onClick={() => addUserIfExists(newUser.trim())}
              disabled={!newUser || !newUser.trim()}
            >
              {isAddLoading ? (
                <LoadingSpinner
                  size="small"
                  colors={{
                    first: 'white',
                    second: 'white',
                  }}
                />
              ) : (
                <>Add User</>
              )}
            </Button>
          </Box>
        </>
      ) : (
        <LoadingSpinner />
      )}
    </>
  );
};

export default withRouter(ManageGroupPage);
